// App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Security, LoginCallback } from '@okta/okta-react';
import { OktaAuth } from '@okta/okta-auth-js';
import { ThemeProvider } from '@mui/material/styles';
import NavMenu from './Components/Common/NavMenu';
import { lightTheme, darkTheme } from './Styles/Theme/ThemeConfig';
import { SiteRoutes } from './Config/siteRoutes';
import ProtectedRoute from './Components/Common/ProtectedRoute';
import './Styles/Theme/App.css';
import './Styles/Theme/theme.css';
import Home from './Pages/Home';
import Login from './Pages/Login';
import Profile from './Profile';

const oktaAuth = new OktaAuth({
  issuer: process.env.REACT_APP_OKTA_ISSUER || 'https://gamesglobal.okta.com',
  clientId: process.env.REACT_APP_OKTA_CLIENT_ID || '0oalyf39xh3TU1pvs417',
  redirectUri: window.location.origin + '/callback',
  postLogoutRedirectUri: window.location.origin + '/callback', // Add this line for logout redirect
  // scopes: ['openid', 'profile', 'email', 'read:protected'], //if scopes are needed
  pkce: true,
});

const App = () => {
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('theme') === 'dark'
  );

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-theme',
      isDarkMode ? 'dark' : 'light'
    );
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Function to handle if authentication is required but not present
  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    window.location.replace(originalUri || '/');
  };

  return (
    <Router>
      <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
        <Security
          oktaAuth={oktaAuth}
          restoreOriginalUri={restoreOriginalUri}
        >
          <div className="app-container">
            <NavMenu isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
            <main className="main-content">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/callback" element={<LoginCallback />} />

                {/* Protected profile route */}
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />

                {/* All other routes from SiteRoutes configuration */}
                {SiteRoutes.map(({ path, element: Element, requiresAuth }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      requiresAuth ? (
                        <ProtectedRoute>
                          <Element />
                        </ProtectedRoute>
                      ) : (
                        <Element />
                      )
                    }
                  />
                ))}

                {/* Catch-all route for 404 - optional */}
                <Route
                  path="*"
                  element={
                    <div className="not-found">
                      <h2>Page Not Found</h2>
                      <p>The page you're looking for doesn't exist or you don't have permission to view it.</p>
                    </div>
                  }
                />
              </Routes>
            </main>
          </div>
        </Security>
      </ThemeProvider>
    </Router>
  );
};

export default App;